import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  Spread,
  TextNode,
} from 'lexical';

export const PLACEHOLDER_ATTRIBUTE = 'data-template-placeholder';
export const PLACEHOLDER_CLASS = 'lexical-template-placeholder';
export const PLACEHOLDER_FILLED_CLASS = 'is-filled';

export type SerializedPlaceholderNode = Spread<
  { placeholderName: string },
  SerializedTextNode
>;

export class PlaceholderNode extends TextNode {
  __placeholderName: string;

  static override getType(): string {
    return 'template-placeholder';
  }

  static override clone(node: PlaceholderNode): PlaceholderNode {
    return new PlaceholderNode(node.__placeholderName, node.__text, node.__key);
  }

  static override importJSON(
    serializedNode: SerializedPlaceholderNode,
  ): PlaceholderNode {
    return $createPlaceholderNode(
      serializedNode.placeholderName,
      serializedNode.text,
    ).updateFromJSON(serializedNode);
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      span: (element: HTMLElement) =>
        element.hasAttribute(PLACEHOLDER_ATTRIBUTE)
          ? {
              conversion: (span: HTMLElement) => ({
                node: $createPlaceholderNode(
                  span.getAttribute(PLACEHOLDER_ATTRIBUTE) ?? '',
                  span.textContent ?? undefined,
                ),
              }),
              priority: 2,
            }
          : null,
    };
  }

  constructor(placeholderName: string, text?: string, key?: NodeKey) {
    super(text ?? placeholderName, key);
    this.__placeholderName = placeholderName;
  }

  override exportJSON(): SerializedPlaceholderNode {
    return {
      ...super.exportJSON(),
      placeholderName: this.__placeholderName,
    };
  }

  getPlaceholderName(): string {
    return this.getLatest().__placeholderName;
  }

  isFilled(): boolean {
    return this.getTextContent() !== this.getPlaceholderName();
  }

  override createDOM(
    config: EditorConfig,
    editor?: LexicalEditor,
  ): HTMLElement {
    const element = super.createDOM(config, editor);
    element.classList.add(PLACEHOLDER_CLASS);
    element.classList.toggle(PLACEHOLDER_FILLED_CLASS, this.isFilled());
    return element;
  }

  override updateDOM(
    prevNode: this,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    const isUpdated = super.updateDOM(prevNode, dom, config);
    dom.classList.add(PLACEHOLDER_CLASS);
    dom.classList.toggle(PLACEHOLDER_FILLED_CLASS, this.isFilled());
    return isUpdated;
  }

  // Only untouched placeholders are marked in the HTML, so the save can warn
  // about them; a filled one is plain text already.
  override exportDOM(editor: LexicalEditor): DOMExportOutput {
    const output = super.exportDOM(editor);
    if (output.element instanceof HTMLElement && !this.isFilled()) {
      output.element.setAttribute(
        PLACEHOLDER_ATTRIBUTE,
        this.getPlaceholderName(),
      );
    }
    return output;
  }
}

export function $createPlaceholderNode(
  placeholderName: string,
  text?: string,
): PlaceholderNode {
  return $applyNodeReplacement(new PlaceholderNode(placeholderName, text));
}

export function $isPlaceholderNode(
  node: LexicalNode | null | undefined,
): node is PlaceholderNode {
  return node instanceof PlaceholderNode;
}
